import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2 } from "lucide-react";
import { WEEKDAYS_LONG } from "@/lib/format";
import { Header } from "@/components/admin/ServicesTab";

const emptyDay = { work_start: "09:00", work_end: "19:00", breaks: [] };

export default function HoursTab({ barbers }) {
  const [barberId, setBarberId] = useState(barbers[0]?.id || "");
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/barbers/${barberId}/working-hours`);
      setSchedule(data.schedule);
    } catch { toast.error("Erro ao carregar horários."); }
    finally { setLoading(false); }
  }, [barberId]);

  useEffect(() => { if (barberId) load(); }, [barberId, load]);

  const toggleDay = (i) => {
    setSchedule((s) => ({ ...s, [i]: s[i] ? null : { ...emptyDay } }));
  };
  const setField = (i, field, value) => {
    setSchedule((s) => ({ ...s, [i]: { ...s[i], [field]: value } }));
  };
  const addBreak = (i) => {
    setSchedule((s) => ({ ...s, [i]: { ...s[i], breaks: [...(s[i].breaks || []), { start: "12:00", end: "13:00" }] } }));
  };
  const setBreak = (i, bi, field, value) => {
    setSchedule((s) => {
      const breaks = [...s[i].breaks];
      breaks[bi] = { ...breaks[bi], [field]: value };
      return { ...s, [i]: { ...s[i], breaks } };
    });
  };
  const removeBreak = (i, bi) => {
    setSchedule((s) => ({ ...s, [i]: { ...s[i], breaks: s[i].breaks.filter((_, x) => x !== bi) } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/barbers/${barberId}/working-hours`, { schedule });
      toast.success("Horários salvos.");
    } catch { toast.error("Erro ao salvar."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fade-in">
      <Header title="Horários de atendimento" />
      <div className="card-surface p-4 mb-5 flex flex-wrap items-center gap-3">
        <label className="text-sm text-stone-300 font-sans2">Profissional:</label>
        <select
          data-testid="hours-barber-select"
          value={barberId}
          onChange={(e) => setBarberId(e.target.value)}
          className="bg-[#12141A] border border-[#282C37] rounded-lg px-3 py-2 text-sm text-amber-50 focus:border-[#D4AF37] focus:outline-none"
        >
          {barbers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {loading || !schedule ? (
        <div className="flex justify-center py-10 text-stone-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <>
          <div className="hours-grid">
            {WEEKDAYS_LONG.map((label, i) => {
              const day = schedule[i];
              return (
                <div key={i} data-testid={`weekday-row-${i}`} className="card-surface p-4">
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`weekday-toggle-${i}`}
                        checked={!!day}
                        onChange={() => toggleDay(i)}
                        className="accent-[#D4AF37] w-4 h-4"
                      />
                      <span className="font-sans2 font-medium text-amber-50">{label}</span>
                    </label>
                    {!day && <span className="text-xs text-stone-500 font-sans2">Fechado</span>}
                  </div>
                  {day && (
                    <div className="mt-3 pl-6 space-y-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-stone-400 font-sans2">Das</span>
                        <input type="time" data-testid={`weekday-start-${i}`} value={day.work_start} onChange={(e) => setField(i, "work_start", e.target.value)} className={timeCls} />
                        <span className="text-xs text-stone-400 font-sans2">às</span>
                        <input type="time" data-testid={`weekday-end-${i}`} value={day.work_end} onChange={(e) => setField(i, "work_end", e.target.value)} className={timeCls} />
                      </div>
                      {(day.breaks || []).map((b, bi) => (
                        <div key={bi} className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-[#C57B57] font-sans2">Intervalo</span>
                          <input type="time" value={b.start} onChange={(e) => setBreak(i, bi, "start", e.target.value)} className={timeCls} />
                          <span className="text-xs text-stone-400">–</span>
                          <input type="time" value={b.end} onChange={(e) => setBreak(i, bi, "end", e.target.value)} className={timeCls} />
                          <button onClick={() => removeBreak(i, bi)} className="text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                      <button data-testid={`add-break-${i}`} onClick={() => addBreak(i)} className="text-xs text-[#D4AF37] hover:text-[#E5C158] flex items-center gap-1 font-sans2">
                        <Plus className="w-3.5 h-3.5" /> Adicionar intervalo
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button
            data-testid="save-hours-btn"
            onClick={save}
            disabled={saving}
            className="mt-6 flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0B0E] px-5 py-2.5 rounded-lg font-sans2 font-semibold text-sm transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar horários
          </button>
        </>
      )}
    </div>
  );
}

const timeCls = "bg-[#0A0B0E] border border-[#282C37] rounded-lg px-2 py-1.5 text-sm text-amber-50 focus:border-[#D4AF37] focus:outline-none font-mono2";
